import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CloudRain, ThermometerSun, ZapOff, CheckCircle } from 'lucide-react';

const ProblemCard = ({ icon: Icon, title, description, solution, index, active, setActive }) => (
    <motion.div
        layout
        onClick={() => setActive(index)}
        className={`glass-panel`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        style={{
            padding: '2rem',
            borderRadius: '16px',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            border: active === index ? '1px solid var(--accent)' : '1px solid var(--border-subtle)',
            background: active === index ? 'rgba(212, 255, 158, 0.05)' : 'var(--bg-card)',
            transition: 'all 0.4s ease'
        }}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div style={{
                color: active === index ? 'var(--accent)' : 'var(--text-muted)',
                transition: 'color 0.3s'
            }}>
                <Icon size={32} />
            </div>
            {active === index && (
                <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                >
                    <CheckCircle size={24} color="var(--accent)" />
                </motion.div>
            )}
        </div>

        <h3 style={{
            fontSize: '1.5rem',
            marginBottom: '0.5rem',
            color: active === index ? 'var(--text-main)' : 'var(--text-muted)'
        }}>
            {title}
        </h3>

        <AnimatePresence>
            {active === index && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    style={{ overflow: 'hidden' }}
                >
                    <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                        {description}
                    </p>
                    <div style={{ padding: '1rem', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', borderLeft: '3px solid var(--accent)' }}>
                        <p style={{ fontSize: '0.9rem', color: 'var(--accent)' }}>
                            <strong style={{ display: 'block', marginBottom: '0.2rem', color: 'currentcolor' }}>Seikaia Solution:</strong>
                            {solution}
                        </p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </motion.div>
);

const ProblemSolution = () => {
    const [activeIndex, setActiveIndex] = useState(0);

    const problems = [
        {
            icon: CloudRain,
            title: 'The Silent Killer: Overwatering',
            description: 'The #1 cause of plant death. Roots suffocate without oxygen, leading to rot that is often invisible until it’s too late.',
            solution: 'Real-time soil moisture sensors detect exactly when the root zone is dry, alerting you only when water is actually needed.'
        },
        {
            icon: ThermometerSun,
            title: 'Invisible Stress: Temp Shock',
            description: 'Drafty windows and heating vents create micro-climates that stress plants, causing leaf drop and stunted growth.',
            solution: 'Continuous ambient logging identifies harmful fluctuations and suggests the perfect spot in your home.'
        },
        {
            icon: ZapOff,
            title: 'Starvation: Light Deficiency',
            description: 'Human eyes adapt to darkness, but plants dont. What looks bright to you might be starving your plant of energy.',
            solution: 'Daily Light Integral (DLI) calculation measures the total photosynthetically active radiation your plant receives.'
        },
    ];

    return (
        <section style={{ padding: '8rem 2rem', position: 'relative' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <div style={{ textAlign: 'left', marginBottom: '5rem' }}>
                    <h2 style={{
                        fontSize: '3.5rem',
                        marginBottom: '1.5rem',
                        lineHeight: 1,
                        color: 'var(--text-muted)'
                    }}>
                        Don't let them <br />
                        <span style={{ color: 'var(--text-main)', fontStyle: 'italic', fontFamily: 'Playfair Display, serif' }}>die in silence.</span>
                    </h2>
                    <p style={{ fontSize: '1.2rem', color: 'var(--text-muted)', maxWidth: '500px' }}>
                        Most plant problems are preventable. The gap is data. <br />
                        Select a problem to see how we solve it.
                    </p>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '2rem'
                }}>
                    {problems.map((problem, index) => (
                        <ProblemCard
                            key={index}
                            {...problem}
                            index={index}
                            active={activeIndex}
                            setActive={setActiveIndex}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ProblemSolution;
